import React, { useContext } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { format } from 'date-fns/format';
import Link from 'next/link';
import { Flex, Box } from '@components/layout/Grid';
import { Trans } from '@lingui/react';
import LanguageContext from '@lib/contexts/languageContext';
import ProgramMenu from './ProgramMenu';
import PlayCircleFilled from '@assets/PlayCircleFilled';
import {
  CourseAccess,
  GenericProgram,
  ProgramAccess,
  ProgramCategory,
} from '@gql/generated';
import ProgramDurationTheme from '@organisms/programs/ProgramDurationTheme';
import { CurrentElement } from '@gql/generated';

const ContinueCard = styled(Box)`
  flex-shrink: 0;
  border: 1px solid #dce1e7;
  border-radius: 3px;
  padding: 20px 15px;
  background-color: #fff;

  @media (min-width: ${({ theme }) => theme.viewports.tablet}px) {
    padding: 20px 25px 25px;
  }
`;

const Anchor = styled(Link)`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.neutrals.gray};
  font-weight: 600;
  text-transform: capitalize;
  text-decoration: none;
  cursor: pointer;

  svg {
    width: 20px;
    margin-right: 8px;
  }
`;

const AccessText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.neutrals.gray};
  margin-bottom: 0;
`;

const ProgrammAccessTag = styled.span<{
  programCategory?: GenericProgram['category'];
}>`
  display: inline-block;
  font-size: 11px;
  color: white;
  line-height: 1;
  text-transform: uppercase;
  border-radius: 3px;
  padding: 5px;
  margin-right: 15px;
  background-color: ${({ theme, programCategory }) =>
    programCategory === ProgramCategory['Challenges']
      ? theme.colors.primary['green']
      : theme.colors.primary['blue-2']};
`;

type Props = {
  className?: string;
  currentVideo: CurrentElement;
  programAccess: ProgramAccess | CourseAccess;
  themes?: GenericProgram['themes'];
  duration: GenericProgram['duration'];
  programCategory?: GenericProgram['category'];
  enableGiftPurchase: boolean;
  canExtendAccess: boolean;
};

const ContinueProgramCard = (props: Props) => {
  const router = useRouter();
  const lang = useContext(LanguageContext);
  const {
    className = '',
    currentVideo,
    programAccess,
    themes,
    duration,
    programCategory,
    enableGiftPurchase = false,
    canExtendAccess,
  } = props;

  const programExpiration = programAccess?.accessUntil
    ? format(new Date(programAccess?.accessUntil), 'dd/MM/yyyy')
    : '';

  const videoLink = `/${lang}/${programCategory}/${currentVideo?.path}`;

  return (
    <ContinueCard className={className}>
      <Flex alignItems="center" mb={10}>
        <ProgramDurationTheme duration={duration} themes={themes} />
        <ProgramMenu
          programCategory={programCategory}
          programId={router.query.programId}
          enableGiftPurchase={enableGiftPurchase}
          canExtendAccess={canExtendAccess}
        />
      </Flex>
      {/* ONLY SHOW WHEN AVAILABLE, OTHERWISE NO POINT SHOWING A BROKEN LINK */}
      {currentVideo ? (
        <Anchor href={videoLink}>
          <PlayCircleFilled />
          <Trans id="metadata.resume" />: {currentVideo?.title}
        </Anchor>
      ) : null}
      <AccessText>
        <ProgrammAccessTag programCategory={programCategory}>
          <Trans id="metadata.active" />
        </ProgrammAccessTag>
        {programCategory === ProgramCategory['Courses'] ? (
          <Trans
            id="courses_2021.header.access_until"
            values={{ date: programExpiration }}
          />
        ) : (
          <Trans
            id="programs_2021.header.access_until"
            values={{ date: programExpiration }}
          />
        )}
      </AccessText>
    </ContinueCard>
  );
};

export default ContinueProgramCard;
